import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import URC from 'ui-react-common';
import { actionCreator } from '../../redux/alert';

import './GlobalAlert.scss';

// Use ReactSpark.Alert directly to avoid idString/analyticsString warnings from AlertMessage wrapper
const { Alert } = URC.ui.components;

const AUTO_DISMISS_DELAY = 5000; // 5 seconds after clicking elsewhere

/**
 * GlobalAlert - A generic alert component that displays messages at the top of the page.
 *
 * Can be triggered by:
 * 1. Redux actions: dispatch showAlert(message, type) and dismissAlert()
 * 2. Custom DOM events: 'rl:showAlert' and 'rl:dismissAlert'
 *
 * Event format for 'rl:showAlert':
 *   window.dispatchEvent(new CustomEvent('rl:showAlert', {
 *     detail: { message: 'Error message', type: 'fail' }
 *   }));
 *
 * Event format for 'rl:dismissAlert':
 *   window.dispatchEvent(new CustomEvent('rl:dismissAlert'));
 */
class GlobalAlert extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isFadingOut: false,
        };
        this.containerRef = React.createRef();
        this.timer = null;
        this.fadeOutTimer = null;
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleShowAlert = this.handleShowAlert.bind(this);
        this.handleDismissAlert = this.handleDismissAlert.bind(this);
    }

    componentDidMount() {
        document.addEventListener('click', this.handleClickOutside);
        // Listen for generic alert events (can come from anywhere in the app)
        window.addEventListener('rl:showAlert', this.handleShowAlert);
        window.addEventListener('rl:dismissAlert', this.handleDismissAlert);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        window.removeEventListener('rl:showAlert', this.handleShowAlert);
        window.removeEventListener('rl:dismissAlert', this.handleDismissAlert);
        this.clearTimers();
    }

    handleShowAlert(event) {
        const { showAlert } = this.props;
        const detail = event && event.detail;

        if (!detail || typeof detail.message === 'undefined') {
            console.warn('rl:showAlert event missing detail.message');
            return;
        }

        const { message, type } = detail;
        showAlert(message, type || 'fail');
    }

    handleDismissAlert() {
        const { dismissAlert } = this.props;
        dismissAlert();
    }

    clearTimers() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
        if (this.fadeOutTimer) {
            clearTimeout(this.fadeOutTimer);
            this.fadeOutTimer = null;
        }
    }

    componentDidUpdate(prevProps) {
        // Reset fading state and timer when a new alert is displayed
        // This includes both: showing after hidden, AND replacing an existing alert
        const isNewAlert = this.props.alertId !== prevProps.alertId;
        if (isNewAlert && this.props.isDisplayed) {
            this.setState({ isFadingOut: false });
            this.clearTimers();
        }

        // Clear timers when alert is dismissed (isDisplayed: true → false)
        // This handles manual dismissal via "x" button or rl:dismissAlert event
        if (prevProps.isDisplayed && !this.props.isDisplayed) {
            this.clearTimers();
            this.setState({ isFadingOut: false });
        }
    }

    handleClickOutside(event) {
        const { isDisplayed, dismissAlert } = this.props;

        if (!isDisplayed) return;

        if (this.containerRef.current && !this.containerRef.current.contains(event.target)) {
            // User clicked outside the alert - start auto-dismiss timer
            if (!this.timer) {
                this.timer = setTimeout(() => {
                    this.setState({ isFadingOut: true });
                    // After fade animation completes, dismiss the alert
                    this.fadeOutTimer = setTimeout(() => {
                        dismissAlert();
                        this.setState({ isFadingOut: false });
                        this.timer = null;
                        this.fadeOutTimer = null;
                    }, 300); // Match CSS transition duration
                }, AUTO_DISMISS_DELAY);
            }
        }
    }

    render() {
        const { isDisplayed, message, alertType, dismissAlert } = this.props;
        const { isFadingOut } = this.state;

        if (!isDisplayed) {
            return null;
        }

        const containerClass = `global-alert-container${isFadingOut ? ' fading-out' : ''}`;

        return (
            <div className={containerClass} ref={this.containerRef}>
                <Alert
                    type={alertType}
                    dismissible={true}
                    handleAlertDismissed={dismissAlert}
                >
                    {message}
                </Alert>
            </div>
        );
    }
}

GlobalAlert.propTypes = {
    isDisplayed: PropTypes.bool.isRequired,
    message: PropTypes.string.isRequired,
    alertType: PropTypes.string.isRequired,
    alertId: PropTypes.number.isRequired,
    dismissAlert: PropTypes.func.isRequired,
    showAlert: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    const alert = state.alert || {};
    return {
        isDisplayed: alert.isDisplayed || false,
        message: alert.message || '',
        alertType: alert.type || 'fail',
        alertId: alert.alertId || 0,
    };
};

const mapDispatchToProps = (dispatch) => ({
    dismissAlert: () => dispatch(actionCreator.dismissAlert()),
    showAlert: (message, alertType) => dispatch(actionCreator.showAlert(message, alertType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(GlobalAlert);
